import React from 'react'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import { makeStyles } from '@material-ui/core/styles'
import {
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
} from 'lib/form-field-styles'
import EditAvailabilityFormDialog from '../availability-manager/edit-availability-form-dialog'
import { daysOfWeek } from '../availability-manager/constants'
import { dayNames, formatTime12 } from './utils'
import { availabilityDot } from './availability-colors'

const useStyles = makeStyles(theme => ({
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
    boxShadow: 'none',
  },
  facilityBar: {
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #E0E0E0',
    backgroundColor: '#F1F1F1',
  },
  cardBody: {
    padding: theme.spacing(3),
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(2),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.only('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  title: {
    fontWeight: 700,
    lineHeight: 1.3,
    color: CALLOUT_FOREGROUND,
  },
  description: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  editButton: {
    height: 40,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    boxShadow: 'none',
    padding: '6px 12px',
    borderColor: OUTLINED_INPUT_BORDER,
    color: CALLOUT_FOREGROUND,
    backgroundColor: theme.palette.common.white,
    '& .MuiButton-label': {
      textTransform: 'none',
    },
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: '#F4F6F8',
      borderColor: OUTLINED_INPUT_BORDER,
    },
  },
  list: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid #E0E0E0',
    '&:last-child': { borderBottom: 'none' },
  },
  dayLabel: {
    fontWeight: 500,
    color: CALLOUT_FOREGROUND,
  },
  hoursWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 2,
    boxSizing: 'border-box',
    display: 'inline-block',
    flexShrink: 0,
  },
  dotAvailable: availabilityDot('recurring'),
  dotUnavailable: availabilityDot('blocked'),
  hoursLabel: {
    color: CALLOUT_FOREGROUND,
    fontVariantNumeric: 'tabular-nums',
  },
  unavailable: {
    color: theme.palette.text.secondary,
  },
}))

const formatTimeRange = timeSlots => {
  if (!timeSlots || timeSlots.length === 0) return null
  return timeSlots
    .map(ts => `${formatTime12(ts.startTime)} – ${formatTime12(ts.endTime)}`)
    .join(', ')
}

const aggregateByDay = timeSlots =>
  daysOfWeek.reduce((acc, d) => {
    acc[d] = (timeSlots || []).filter(ts => ts.day === d)
    return acc
  }, {})

const RecurringHoursSidebar = ({
  availability,
  onSuccess,
  facilityControl,
}) => {
  const classes = useStyles()
  const byDay = aggregateByDay(availability.timeSlots)

  return (
    <Card className={classes.card} elevation={0}>
      {facilityControl ? (
        <Box className={classes.facilityBar}>{facilityControl}</Box>
      ) : null}
      <Box className={classes.cardBody}>
        <Box className={classes.header}>
          <Box>
            <Typography variant="h6" className={classes.title}>
              Recurring Bookable Hours
            </Typography>
            <Typography variant="body2" className={classes.description}>
              Your weekly schedule of hours when students can book sessions with
              you.
            </Typography>
          </Box>
          <EditAvailabilityFormDialog
            trigger={
              <Button
                variant="outlined"
                startIcon={<EditOutlinedIcon fontSize="small" />}
                className={classes.editButton}
              >
                Edit Weekly Hours
              </Button>
            }
            initialTimeSlots={availability.timeSlots}
            coachFacilityId={availability.coachFacility?.id}
            facilityTimezone={availability.timeZone}
            onSuccess={onSuccess}
          />
        </Box>
        <Box className={classes.list}>
          {daysOfWeek.map((day, idx) => {
            const slots = byDay[day]
            const isAvailable = slots.length > 0
            return (
              <Box key={day} className={classes.row}>
                <Typography variant="body2" className={classes.dayLabel}>
                  {dayNames[idx]}
                </Typography>
                <Box className={classes.hoursWrap}>
                  <span
                    className={clsx(
                      classes.dot,
                      isAvailable
                        ? classes.dotAvailable
                        : classes.dotUnavailable,
                    )}
                    aria-hidden
                    data-testid={`availability-day-indicator-${day.toLowerCase()}`}
                    data-available={isAvailable ? 'true' : 'false'}
                  />
                  {isAvailable ? (
                    <Typography variant="body2" className={classes.hoursLabel}>
                      {formatTimeRange(slots)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" className={classes.unavailable}>
                      Unavailable
                    </Typography>
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Card>
  )
}

export default RecurringHoursSidebar
